import axios, { AxiosError, AxiosResponse } from 'axios';
import { QueryClient } from '@tanstack/react-query';

export const PAGE_LIMIT = 20;

export const isProd = () => import.meta.env.PROD;

const ENV = import.meta.env;

export const CONFIG = {
  API_URL: isProd() ? ENV.VITE_API_URL : 'http://localhost:4100',
  FILES_URL: isProd() ? ENV.VITE_FILE_URL : 'https://cdn.worklage.com/kidster/',
  PLAY_STORE_URL: 'https://bit.ly/kidster-android',
  APP_STORE_URL: 'https://bit.ly/kidster-ios'
};

export const instance = axios.create({
  baseURL: CONFIG.API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    Authorization: `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1dWlkIjoiYmYwYmNjMTQtMTcyNC00MGJmLTg2YjAtYzQ1YTg5MmUzNGRlIiwiaWF0IjoxNzQ0NzgwNDI1LCJleHAiOjE3NzYzMTY0MjV9.XIRydDKg_8AoMULlVJGn-lAU-MfslO1f5O1xpq-9e40`
  }
} as any);

instance.interceptors.request.use(
  (request: any) => {
    // !isProd() && console.log('REQ', request);
    return request;
  },
  (error: AxiosError) => {
    // !isProd() && console.log('ERROR', error);
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError | any) => {
    if (error && error.response && !!error.response.data) {
      return handleError(error.response.data);
    } else {
      const customError = createError(500, 'Something went wrong, try again in some time.');
      return handleError(customError);
    }
  }
);

export const createError = (status: number, message: string) => {
  return { status, message };
};

export interface HTTP_ERROR {
  status: number;
  message: string;
}

export const handleError = (error: HTTP_ERROR) => {
  return Promise.reject(error);
};

export const restClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    }
  }
});

export enum QUERY_KEYS {
  DRAWINGS = 'DRAWINGS',
  CATEGORIES = 'CATEGORIES',
  IMAGES = 'IMAGES'
}

export const MULTIPART_HEADERS = { headers: { 'content-type': 'multipart/form-data' } };

export const extendFormData = (formData: any, data: any) => {
  for (let key in data) {
    formData.append(key, JSON.stringify(data[key] || ''));
  }
  return formData;
};

export const getNextPageParam = (lastPage: any, pages: any) => {
  if (lastPage?.data?.length === PAGE_LIMIT) {
    return pages.length + 1;
  }
  return undefined;
};

export const formatQueryData = (data: any) => {
  return data?.data ? (Array.isArray(data?.data) ? [...data.data] : { ...data.data }) : undefined;
};

export const formatInfiniteQueryData = (data: any) => {
  return (data && (data.pages?.map((g: any) => (g.data && g.data.map((data: any) => data || [])) || []) || []).flat()) || [];
};

export const getImageUrl = (file: string) => {
  return !!file ? CONFIG.FILES_URL + 'images/' + file : '/logo.png';
};

export const getDrawingUrl = (file: string) => {
  return !!file ? CONFIG.FILES_URL + 'drawings/' + file : '/logo.png';
};

export const getFileExtension = (filename: string) => {
  return filename?.split('.')?.pop()?.toLowerCase() || '';
};

export const getUniqueFileName = () => {
  return `NJ${Date.now()}${Math.floor(Math.random() * 99999) + 10000}`;
};

export const getFileName = (fileName: string) => {
  return `${getUniqueFileName()}.${getFileExtension(fileName)}`;
};

export const compressFile = async (file: File) => {
  if (!file) return null;

  const maxDimension = 800;
  const imageBitmap = await createImageBitmap(file);

  // Get original dimensions
  const originalWidth = imageBitmap.width;
  const originalHeight = imageBitmap.height;

  // Calculate new dimensions maintaining aspect ratio
  let newWidth = originalWidth;
  let newHeight = originalHeight;

  if (originalWidth > maxDimension || originalHeight > maxDimension) {
    if (originalWidth > originalHeight) {
      newWidth = maxDimension;
      newHeight = Math.round((maxDimension * originalHeight) / originalWidth);
    } else {
      newHeight = maxDimension;
      newWidth = Math.round((maxDimension * originalWidth) / originalHeight);
    }
  }

  // Create a transparent canvas
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { alpha: true }); // Ensure transparency is enabled

  if (!ctx) {
    console.error('Canvas rendering context not available.');
    return null;
  }

  canvas.width = newWidth;
  canvas.height = newHeight;

  // Ensure the canvas background remains transparent
  ctx.clearRect(0, 0, newWidth, newHeight);

  // Draw the image onto the transparent canvas
  ctx.drawImage(imageBitmap, 0, 0, newWidth, newHeight);

  // Convert canvas to a transparent PNG Blob
  const blob = await new Promise<Blob | null>(resolve => {
    canvas.toBlob(b => resolve(b), 'image/png', 0.5); // PNG keeps transparency
  });

  if (!blob) {
    console.error('Failed to create compressed image blob.');
    return null;
  }

  // Convert Blob to File
  const compressedFile = new File([blob], `compressed-${file.name}`, {
    type: 'image/png',
    lastModified: Date.now()
  });

  return {
    file: compressedFile,
    metadata: {
      width: newWidth,
      height: newHeight,
      size: compressedFile.size,
      type: compressedFile.type,
      name: compressedFile.name
    }
  };
};
